import { atom, useAtom, PrimitiveAtom } from "jotai";
import {
  atomFamily,
  atomWithStorage,
  useAtomValue,
  useUpdateAtom,
} from "jotai/utils";

import { Api } from "interfaces";

import * as flowConfig from "flows/driver-analysis-binary/config";
import { BinaryOutcome } from "flows/driver-analysis/interfaces";
import { useCallback } from "react";

const inputFile = atomWithStorage<string>(
  `${flowConfig.FLOW_NAME}/create/input-file`,
  null
);

export const useInputFile = () => useAtom(inputFile);

export const useGetInputFile = () => useAtomValue(inputFile);

const outcomeDefinition = atomFamily((fileId: string) =>
  atomWithStorage<BinaryOutcome>(
    `${flowConfig.FLOW_NAME}/create/${fileId}/outcomes`,
    null
  )
);

export const useOutcome = () => {
  const fileId = useGetInputFile();
  return useAtom(outcomeDefinition(fileId || ""));
};

export const useGetOutcome = () => {
  const fileId = useGetInputFile();
  return useAtomValue(outcomeDefinition(fileId || ""));
};

const dropNulls = atomFamily((fileId: string) =>
  atomWithStorage(`${flowConfig.FLOW_NAME}/create/${fileId}/drop-null`, true)
);

export const useDropNulls = () => {
  const fileId = useGetInputFile();
  return useAtom(dropNulls(fileId || ""));
};

export const useGetDropNulls = () => {
  const fileId = useGetInputFile();
  return useAtomValue(dropNulls(fileId || ""));
};

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

export const useCreateStatus = () => useAtom(projectCreateStatus);

export const useGetCreateStatus = () => useAtomValue(projectCreateStatus);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useCreateError = () => useAtom(projectCreateError);

export const useGetCreateError = () => useAtomValue(projectCreateError);

const createSpec = atom((get) => {
  const currentFile = get(inputFile);
  if (!currentFile) {
    return null;
  }
  const outcome = get(outcomeDefinition(currentFile));
  if (!outcome) {
    return null;
  }

  return {
    flow: flowConfig.FLOW_NAME,
    data_files: { [flowConfig.INPUT_DATA]: currentFile },
    meta_data: { outcomes: [outcome] },
  };
});

export const useGetCreateSpec = () => useAtomValue(createSpec);

const augmentSpec = atom((get) => {
  if (!get(createSpec)) {
    return null;
  }
  const currentFile = get(inputFile);
  const outcome = get(outcomeDefinition(currentFile));

  const newColumns = [
    {
      name: outcome.variable,
      type: "filter-flags",
      details: { filters: outcome.definition },
    },
  ];
  const columns_to_drop = Array.from(
    new Set(outcome.definition.map((d) => d.column))
  );

  let spec = { new_columns: newColumns, columns_to_drop };
  if (get(dropNulls(currentFile))) {
    spec["missing_treatment"] = columns_to_drop.map((column) => ({
      column,
      treatment: "drop-rows",
    }));
  }

  return {
    key: flowConfig.AUGMENTED_FILE,
    output_file_name: `${currentFile}-with-filter-flags`,
    source_data: [currentFile],
    create_type: "AUGMENT",
    create_spec: spec,
  };
});

export const useGetAugmentSpec = () => useAtomValue(augmentSpec);

export const useResetStatus = () => {
  const setStatus = useUpdateAtom(projectCreateStatus);
  const setError = useUpdateAtom(projectCreateError);

  return useCallback(() => {
    setStatus("not-started");
    setError(null);
  }, [setStatus, setError]);
};
